import { useChannelContext } from "client/components/channel"
import {
  Chat_ChatBan as IChatBan,
  useChat_ChatBansQuery as useChatBansQuery,
} from "client/graphql"
import { useCallback } from "react"
import { FormattedMessage } from "react-intl"
import ResponsiveLoader from "shared/components/ResponsiveLoader"
import { Heading, P } from "shared/styles/textStyles"
import styled from "styled-components"

import { PanelModal } from "../../../shared/components/PanelModal"

import { ChatBanItem } from "./ChatBanItem"
import { useChatContext } from "./ChatContext"

const Header = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.med};
  margin-bottom: 5px;
  padding: 0px 0 10px;

  ${Heading} {
    margin: 0;
  }
`

const ChatBanList = styled.ul`
  margin: unset;
  flex-grow: 1;
  align-self: flex-start;
`

const Body = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  overflow-y: auto;
`

const EmptyStateText = styled(P)`
  font-size: 0.92rem;
  margin: 5px 0;
`

const LoaderWrapper = styled.div`
  width: 100%;
  height: 100px;
  position: relative;
`

export type BannedModalProps = {
  closeModal: () => void
  unbanUser: (user: { userId: string; sourceName: string }) => void
  loading: boolean
  chatBans: IChatBan[]
}

// This somewhat unnecessary component has been split out of its parent
// so that we may render in storybook without having to mock out Apollo
// TO DO: mock out Apollo in Storybook.
export const BannedModal = ({
  unbanUser,
  loading,
  chatBans = [],
  closeModal = () => {},
}: BannedModalProps) => (
  <PanelModal
    closeModal={closeModal}
    closeButtonRobotTag="chat-banned-users-modal-close-button"
  >
    <Header>
      <Heading>
        <FormattedMessage
          id="chatBan.bannedUsers"
          defaultMessage="Banned users"
        />
      </Heading>
    </Header>
    <Body>
      {loading ? (
        <LoaderWrapper>
          <ResponsiveLoader />
        </LoaderWrapper>
      ) : (
        <>
          {chatBans?.length === 0 && (
            <EmptyStateText>
              <FormattedMessage
                id="chatBan.emptyState"
                defaultMessage="No users have been banned."
              />
            </EmptyStateText>
          )}
          {chatBans?.length > 0 && (
            <ChatBanList>
              {chatBans.map((chatBan) => (
                <ChatBanItem
                  key={chatBan.id}
                  chatBan={chatBan}
                  unbanUser={unbanUser}
                />
              ))}
            </ChatBanList>
          )}
        </>
      )}
    </Body>
  </PanelModal>
)

const BannedUsersModal = () => {
  const {
    setIsBannedUsersModalOpen,
    setIsUnbanModalOpen,
    setUnbanModalUser,
  } = useChatContext()

  const { channelId } = useChannelContext()

  const { data, loading } = useChatBansQuery({ variables: { channelId } })
  const chatBans = data?.chat_chatBans ?? []

  const closeModal = useCallback(() => {
    setIsBannedUsersModalOpen(false)
  }, [])

  const unbanUser = useCallback(
    (user: { userId: string; sourceName: string }) => {
      setIsBannedUsersModalOpen(false)
      setIsUnbanModalOpen(true)
      setUnbanModalUser(user)
    },
    [],
  )

  return (
    <BannedModal
      closeModal={closeModal}
      chatBans={chatBans}
      loading={loading}
      unbanUser={unbanUser}
    />
  )
}

export default BannedUsersModal
